import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MedicationCard from './components/MedicationCard';
import PrescriptionPanel from './components/PrescriptionPanel';
import InteractionAlert from './components/InteractionAlert';
import ReconciliationPanel from './components/ReconciliationPanel';
import AdherenceTracker from './components/AdherenceTracker';
import TreatmentDecisionSupport from './components/ClinicalDecisionSupport';

const PrescriptionManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('current');
  const [showPrescriptionPanel, setShowPrescriptionPanel] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "46 years"
  };

  const patientAlerts = [
    {
      id: 'alert-1',
      severity: 'critical',
      title: 'Drug Interaction Alert',
      message: 'Critical interaction detected between anticoagulant and prescribed antibiotic - increased bleeding risk',
      timestamp: '10 min ago',
      actionRequired: 'Review and adjust dosing',
      dismissible: false
    },
    {
      id: 'alert-2',
      severity: 'warning',
      title: 'Prescription Expiring Soon',
      message: 'Pain medication prescription expires in 3 days - refill needed',
      timestamp: '2 hours ago',
      actionRequired: 'Process refill request',
      dismissible: true
    }
  ];

  const currentMedications = [
    {
      id: 'med-1',
      name: 'Amoxicillin',
      genericName: 'Amoxicillin',
      dosage: '500mg',
      frequency: 'Three Times Daily',
      route: 'Oral (PO)',
      status: 'active',
      prescribedBy: 'Dr. Michael Chen',
      startDate: '01/15/2024',
      refillsRemaining: 0,
      totalRefills: 0,
      lastFilled: '01/15/2026',
      instructions: 'Take with food. Complete full course for dental infection treatment.'
    },
    {
      id: 'med-2',
      name: 'Ibuprofen',
      genericName: 'Ibuprofen',
      dosage: '600mg',
      frequency: 'As Needed (PRN)',
      route: 'Oral (PO)',
      status: 'expiring',
      prescribedBy: 'Dr. Emily Rodriguez',
      startDate: '03/20/2024',
      refillsRemaining: 1,
      totalRefills: 3,
      lastFilled: '12/20/2025',
      instructions: 'Take with food for pain management. Do not exceed 2400mg per day.',
      alertLevel: 'warning',
      alertMessage: 'Prescription expires in 3 days. Refill needed.'
    },
    {
      id: 'med-3',
      name: 'Chlorhexidine Gluconate',
      genericName: 'Chlorhexidine Gluconate',
      dosage: '0.12%',
      frequency: 'Twice Daily (BID)',
      route: 'Oral Rinse',
      status: 'active',
      prescribedBy: 'Dr. Michael Chen',
      startDate: '02/10/2024',
      refillsRemaining: 2,
      totalRefills: 3,
      lastFilled: '01/10/2026',
      instructions: 'Rinse for 30 seconds after brushing. Do not swallow.'
    },
    {
      id: 'med-4',
      name: 'Hydrocodone/Acetaminophen',
      genericName: 'Hydrocodone/Acetaminophen',
      dosage: '5mg/325mg',
      frequency: 'Every 4-6 Hours PRN',
      route: 'Oral (PO)',
      status: 'active',
      prescribedBy: 'Dr. Sarah Williams',
      startDate: '11/05/2025',
      refillsRemaining: 0,
      totalRefills: 0,
      lastFilled: '01/05/2026',
      instructions: 'Take for severe pain after oral surgery. Do not drive or operate machinery.'
    }
  ];

  const drugInteractions = [
    {
      id: 'interaction-1',
      severity: 'critical',
      title: 'Anticoagulant + Antibiotic Interaction',
      description: 'Concurrent use of anticoagulants and certain antibiotics may significantly increase bleeding risk during and after dental procedures.',
      medications: ['Warfarin', 'Amoxicillin 500mg'],
      recommendation: 'Monitor INR closely. Consider alternative antibiotic or adjust anticoagulant dosing. Educate patient on bleeding precautions.'
    },
    {
      id: 'interaction-2',
      severity: 'major',
      title: 'NSAID + Anticoagulant Interaction',
      description: 'NSAIDs like Ibuprofen may increase bleeding risk when combined with anticoagulants.',
      medications: ['Ibuprofen 600mg', 'Warfarin'],
      recommendation: 'Consider acetaminophen as alternative for pain management. If NSAID necessary, use lowest effective dose with close monitoring.'
    }
  ];

  const homeMedications = [
    { id: 'home-1', name: 'Amoxicillin', dosage: '500mg', frequency: 'Three Times Daily' },
    { id: 'home-2', name: 'Ibuprofen', dosage: '600mg', frequency: 'As Needed' },
    { id: 'home-3', name: 'Chlorhexidine', dosage: '0.12%', frequency: 'Twice Daily' }
  ];

  const hospitalOrders = [
    { id: 'hosp-1', name: 'Amoxicillin', dosage: '500mg', frequency: 'Three Times Daily' },
    { id: 'hosp-2', name: 'Ibuprofen', dosage: '600mg', frequency: 'As Needed' },
    { id: 'hosp-3', name: 'Hydrocodone/APAP', dosage: '5mg/325mg', frequency: 'Every 4-6 Hours' }
  ];

  const adherenceData = {
    overall: 87,
    last30Days: 92,
    missedDoses: 4,
    medications: [
      {
        name: 'Amoxicillin 500mg',
        dosage: 'Three Times Daily',
        adherence: 95,
        lastRefill: '01/15/2026',
        nextRefill: 'N/A - Short Course'
      },
      {
        name: 'Ibuprofen 600mg',
        dosage: 'As Needed',
        adherence: 88,
        lastRefill: '12/20/2025',
        nextRefill: '02/20/2026'
      },
      {
        name: 'Chlorhexidine 0.12%',
        dosage: 'Twice Daily',
        adherence: 92,
        lastRefill: '01/10/2026',
        nextRefill: '03/10/2026'
      }
    ]
  };

  const patientFactors = {
    age: 46,
    weight: 68,
    egfr: 75,
    liverFunction: 'Normal'
  };

  const clinicalRecommendations = [
    {
      type: 'renal',
      title: 'Renal Dose Adjustment for Antibiotics',
      description: 'Patient eGFR of 75 mL/min requires monitoring but no dose adjustment currently needed.',
      calculation: {
        steps: [
          'Current eGFR: 75 mL/min/1.73m²',
          'Amoxicillin dose adjustment needed if eGFR < 30 mL/min',
          'Current dose: 500mg TID is appropriate',
          'Monitor renal function during treatment'
        ],
        result: '500mg three times daily (no adjustment needed)'
      },
      guidelines: [
        'Monitor renal function if prolonged antibiotic therapy',
        'Adjust dose if eGFR declines below 30 mL/min',
        'Consider alternative antibiotics for severe renal impairment'
      ],
      references: 'FDA Amoxicillin Prescribing Information, 2024'
    },
    {
      type: 'dosing',
      title: 'Weight-Based Pain Management Dosing',
      description: 'Pain medication dosing recommendation based on patient weight.',
      calculation: {
        steps: [
          'Patient weight: 68 kg',
          'Ibuprofen: 10mg/kg/dose (max 800mg)',
          'Recommended dose: 600mg every 6 hours',
          'Maximum daily dose: 2400mg'
        ],
        result: '600mg every 6 hours as needed for pain'
      },
      guidelines: [
        'Take with food to minimize GI upset',
        'Monitor for signs of GI bleeding',
        'Avoid in patients with active ulcers',
        'Consider acetaminophen alternative if contraindicated'
      ],
      references: 'ADA Pain Management Guidelines, 2024'
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Medications' },
    { value: 'active', label: 'Active Only' },
    { value: 'expiring', label: 'Expiring Soon' },
    { value: 'critical', label: 'Critical Alerts' },
    { value: 'discontinued', label: 'Discontinued' }
  ];

  const quickActions = [
    {
      id: 'new-prescription',
      label: 'New Prescription',
      icon: 'Plus',
      onClick: () => setShowPrescriptionPanel(true)
    },
    {
      id: 'refill-request',
      label: 'Refill Request',
      icon: 'RefreshCw',
      onClick: () => console.log('Refill request')
    },
    {
      id: 'med-history',
      label: 'Medication History',
      icon: 'History',
      onClick: () => console.log('View history')
    },
    {
      id: 'print-list',
      label: 'Print Med List',
      icon: 'Printer',
      onClick: () => console.log('Print list')
    }
  ];

  const handleEditMedication = (medication) => {
    console.log('Edit medication:', medication);
  };

  const handleDiscontinueMedication = (medication) => {
    console.log('Discontinue medication:', medication);
  };

  const handleRefillRequest = (medication) => {
    console.log('Refill request for:', medication);
  };

  const handlePrescribe = (prescriptionData) => {
    console.log('New prescription:', prescriptionData);
    setShowPrescriptionPanel(false);
  };

  const handleViewInteractionDetails = (interaction) => {
    console.log('View interaction details:', interaction);
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismiss alert:', alertId);
  };

  const handleReconcile = (reconciliationData) => {
    console.log('Reconciliation data:', reconciliationData);
  };

  const handleApplyRecommendation = (recommendation) => {
    console.log('Apply recommendation:', recommendation);
  };

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
  };

  const handleSessionExpired = () => {
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  const filteredMedications = currentMedications?.filter(med => {
    const matchesStatus = filterStatus === 'all' || med?.status === filterStatus;
    const matchesSearch = med?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         med?.genericName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />

      <div className={`transition-all duration-250 ease-out ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <PatientContextBar 
          patient={currentPatient} 
          alerts={patientAlerts}
          onAlertClick={handleAlertClick}
        />

        <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                <Icon name="Pill" size={32} className="text-primary" />
                Prescription Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Manage dental prescriptions, track adherence, and monitor drug interactions
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowPrescriptionPanel(true)}
            >
              New Prescription
            </Button>
          </div>

          <div className="flex flex-col lg:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Icon 
                name="Search" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
              />
              <input
                type="text"
                placeholder="Search medications by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full h-12 pl-10 pr-4 bg-card border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
              />
            </div>
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filter by status"
              className="lg:w-64"
            />
          </div>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {['current', 'interactions', 'reconciliation', 'adherence', 'clinical-support']?.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-shrink-0 px-4 md:px-6 py-2 md:py-3 rounded-lg text-sm font-medium transition-smooth ${
                  activeTab === tab
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {tab?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')}
              </button>
            ))}
          </div>

          {activeTab === 'current' && (
            <div className="space-y-6">
              {showPrescriptionPanel && (
                <PrescriptionPanel
                  onPrescribe={handlePrescribe}
                  onClose={() => setShowPrescriptionPanel(false)}
                />
              )}

              <div className="grid grid-cols-1 gap-4 md:gap-6">
                {filteredMedications?.length > 0 ? (
                  filteredMedications?.map((medication) => (
                    <MedicationCard
                      key={medication?.id}
                      medication={medication}
                      onEdit={handleEditMedication}
                      onDiscontinue={handleDiscontinueMedication}
                      onRefill={handleRefillRequest}
                    />
                  ))
                ) : (
                  <div className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
                      <Icon name="Pill" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No medications found</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      {searchQuery ? 'Try adjusting your search criteria' : 'No medications match the selected filter'}
                    </p>
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => setShowPrescriptionPanel(true)}
                    >
                      Add New Medication
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'interactions' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-lg">
                    <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">Drug Interactions</h2>
                    <p className="text-sm text-muted-foreground">Real-time interaction checking and alerts</p>
                  </div>
                </div>
                <InteractionAlert
                  interactions={drugInteractions}
                  onViewDetails={handleViewInteractionDetails}
                  onDismiss={handleDismissAlert}
                />
              </div>
            </div>
          )}

          {activeTab === 'reconciliation' && (
            <ReconciliationPanel
              homeMedications={homeMedications}
              hospitalOrders={hospitalOrders}
              onReconcile={handleReconcile}
            />
          )}

          {activeTab === 'adherence' && (
            <AdherenceTracker adherenceData={adherenceData} />
          )}

          {activeTab === 'clinical-support' && (
            <TreatmentDecisionSupport
              patientFactors={patientFactors}
              recommendations={clinicalRecommendations}
            />
          )}
        </main>
      </div>
      <QuickActions actions={quickActions} position="bottom-right" />
      <SessionManager
        sessionTimeout={900000}
        warningTime={120000}
        onSessionExpired={handleSessionExpired}
        onSessionExtended={handleSessionExtended}
      />
    </div>
  );
};

export default PrescriptionManagement;